import React from "react";

import { useMutation } from "@apollo/client";
import {
  ArrowUpward,
  ArrowDownward,
  PushPinOutlined,
  People,
  ExpandMore,
} from "@mui/icons-material";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  Box,
  TableCell,
  Avatar,
  Skeleton,
  Stack,
  Select,
  MenuItem,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { makeStyles } from "tss-react/mui";

import { ContainedIconButton } from "@/components/Button/ContainedIconButton";
import { IconButton } from "@/components/Button/IconButton";
import { OutlinedIconButton } from "@/components/Button/OutlinedIconButton";
import { Status } from "@/components/Chip/Status";
import { BodyHyperlink } from "@/components/Hyperlinks/BodyHyperlink";
import { CaptionHyperlink } from "@/components/Hyperlinks/CaptionHyperlink";
import pinnedClients from "@/components/Sidebar/ClickComponents/Client/store/PinnedClients";
import { Spacer } from "@/components/Spacer/Spacer";
import { DividerTableCell } from "@/components/Table/DividerTableCell";
import {
  Body,
  Caption,
  LargeTitle,
  Overline1Bold,
  SubheadBold,
} from "@/components/Typography";
import { ClientsPageClientFragment, TrackingStatus } from "@/gql/graphql";
import { useDefaultStyles } from "@/hooks/useDefaultStyles";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { format } from "@/utils/datetime";
import { displayError } from "@/utils/errors";

import { updateClientTrackingStatusMutation } from "../graphql/clientsPageMutations";

const useStyles = makeStyles()({
  iconSize: {
    height: "80px",
    width: "80px",
  },
});

const trackingStatusChip = {
  [TrackingStatus.Active]: <Status label="Active" />,
  [TrackingStatus.Cancelled]: <Status color="secondary" label="Cancelled" />,
  [TrackingStatus.NoAction]: <Status color="error" label="No Action" />,
  [TrackingStatus.Onboarding]: <Status label="Onboarding" />,
  [TrackingStatus.Pending]: <Status color="warning" label="Pending" />,
  [TrackingStatus.TestOnly]: <Status color="secondary" label="Test only" />,
};

interface ClientTableProps {
  clients: ClientsPageClientFragment[];
  isLoading: boolean;
  sortOrder: "asc" | "desc";
  onSortToggle: () => void;
  onViewClientActivity: (client: ClientsPageClientFragment) => void;
}

export const ClientTable: React.FC<ClientTableProps> = observer(
  ({ clients, isLoading, sortOrder, onSortToggle, onViewClientActivity }) => {
    const { classes } = useStyles();
    const { classes: defaultClasses } = useDefaultStyles();
    const snackbar = useGlobalSnackbar();
    const headers = [
      {
        key: "name",
        label: "Name",
        button: (
          <IconButton
            icon={
              sortOrder === "asc" ? (
                <ArrowUpward color="primary" />
              ) : (
                <ArrowDownward color="primary" />
              )
            }
            size="smaller"
            onClick={onSortToggle}
          />
        ),
        width: "30%",
      },
      {
        key: "status",
        label: "Status",
        width: "10%",
      },
      {
        key: "dateOnboarded",
        label: "Date Onboarded",
        width: "10%",
      },
      {
        key: "subscriptionPlan",
        label: "Subscription Plan",
        width: "10%",
      },
      {
        key: "clientCategory",
        label: "Client Category",
        width: "10%",
      },
      {
        key: "month",
        label: "Month",
        width: "10%",
      },
      {
        key: "latestTimeline",
        label: "Latest Timeline",
        width: "20%",
      },
    ];

    // API
    const [_updateTrackingStatus] = useMutation(
      updateClientTrackingStatusMutation
    );

    // Functions
    const handleTogglePin = (clientId: string) => {
      pinnedClients.toggle(clientId);
    };

    const handleUpdateTrackingStatus = async (
      clientId: string,
      status: TrackingStatus
    ) => {
      try {
        _updateTrackingStatus({
          variables: { input: { clientId, trackingStatus: status } },
          optimisticResponse: {
            __typename: "Mutation",
            updateTrackingStatus: {
              __typename: "UpdateTrackingStatusPayload",
              client: {
                __typename: "Client",
                id: clientId,
                trackingStatus: status,
              },
            },
          },
        });
      } catch (e) {
        snackbar.open(displayError(e, "Unable to update status."));
      }
    };

    const loadingClientsComponent = (
      <>
        {Array.from({ length: 10 }).map((_, index) => (
          <Box key={index} marginBottom={2}>
            <Skeleton variant="rectangular" width="100%" height={80} />
          </Box>
        ))}
      </>
    );

    const emptyClientComponent = (
      <Box display="flex" alignItems="center" justifyContent="center">
        <Spacer h={250} />
        <Stack alignItems="center">
          <People className={classes.iconSize} color="secondary" />
          <Spacer h={24} />
          <LargeTitle text="No Clients" />
        </Stack>
      </Box>
    );

    return (
      <>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableCell key={header.key} width={header.width}>
                    <Box display="flex" alignItems="center">
                      <Overline1Bold text={header.label} />
                      {header.button}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {!isLoading && clients.length !== 0 && (
              <TableBody>
                {
                  <>
                    {clients.map((client, index) => (
                      <>
                        <TableRow>
                          <DividerTableCell width="300px">
                            <Box display="flex" alignItems="center">
                              {pinnedClients.has(client.id) ? (
                                <ContainedIconButton
                                  icon={
                                    <PushPinOutlined
                                      onClick={() => handleTogglePin(client.id)}
                                      fontSize="small"
                                    />
                                  }
                                  size="smaller"
                                  onClick={() => handleTogglePin(client.id)}
                                />
                              ) : (
                                <OutlinedIconButton
                                  icon={
                                    <PushPinOutlined
                                      onClick={() => handleTogglePin(client.id)}
                                      fontSize="small"
                                    />
                                  }
                                  size="smaller"
                                  onClick={() => handleTogglePin(client.id)}
                                />
                              )}
                              <Spacer w={14} />
                              <Avatar
                                src={client?.profile?.avatarUrl ?? undefined}
                              />
                              <Spacer w={16} />
                              <Box>
                                <BodyHyperlink
                                  text={client?.profile?.fullName}
                                  href={`/clients/${client?.id}`}
                                />
                                <SubheadBold
                                  text={client?.email}
                                  className={defaultClasses.textWrap}
                                  maxWidth="300px"
                                />
                                <SubheadBold
                                  text={client?.profile?.mobileNumber}
                                />
                              </Box>
                            </Box>
                          </DividerTableCell>
                          <DividerTableCell>
                            <Select
                              variant="standard"
                              IconComponent={() => (
                                <ExpandMore
                                  className={defaultClasses.cursorPointer}
                                  color="primary"
                                  fontSize="small"
                                />
                              )}
                              fullWidth
                              value={client.trackingStatus}
                              onChange={(e) => {
                                handleUpdateTrackingStatus(
                                  client.id,
                                  e.target.value as TrackingStatus
                                );
                              }}
                            >
                              {Object.values(TrackingStatus).map((status) => (
                                <MenuItem key={status} value={status}>
                                  <React.Fragment key={status}>
                                    {trackingStatusChip[status]}
                                  </React.Fragment>
                                </MenuItem>
                              ))}
                            </Select>
                          </DividerTableCell>
                          <DividerTableCell>
                            <Body
                              text={format(
                                { datetime: client?.createdAt },
                                "MMM dd, yyyy"
                              )}
                            />
                          </DividerTableCell>
                          <DividerTableCell>
                            <Body
                              text={client?.selectedPlanName ?? "N/A"}
                              color={
                                !client?.selectedPlanName
                                  ? colors.mapped.text.disabled
                                  : colors.mapped.text.body
                              }
                            />
                          </DividerTableCell>
                          <DividerTableCell>
                            <Body
                              text={`${
                                origin === "marketplace" ? "Paid" : "Free"
                              } Client`}
                            />
                          </DividerTableCell>
                          <DividerTableCell>
                            <Body text={`Month ${client?.month}`} />
                          </DividerTableCell>
                          <DividerTableCell>
                            <Caption
                              text={format(
                                { datetime: client?.lastEvent?.createdAt },
                                "Pp"
                              )}
                            />
                            <Spacer h={4} />
                            <Body text={client?.lastEvent?.message} />
                            <Spacer h={4} />
                            <CaptionHyperlink
                              text="View More"
                              onClick={() => onViewClientActivity(client)}
                            />
                          </DividerTableCell>
                        </TableRow>
                        {clients.length !== index && <Spacer h={8} />}
                      </>
                    ))}
                  </>
                }
              </TableBody>
            )}
          </Table>
          <>{isLoading && loadingClientsComponent}</>
          <>{clients.length === 0 && emptyClientComponent}</>
        </TableContainer>
      </>
    );
  }
);
